import {
  CSSProperties,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { Button } from '~/components/Button'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { useAddToCart, usePage, useZendesk } from '~/hooks'
import { MIN_DESKTOP_WIDTH } from '~/providers/PageProvider'
import { Product } from '~/types'
import { ProductDashboardQuantitySelector } from '../ProductDashboardQuantitySelector'
import styles from './ProductDashboardStickyControls.module.scss'

// Matches the bottom gap applied to the pinned bar in the module stylesheet.
const STICKY_CONTROLS_BOTTOM_OFFSET = 0

export interface ProductDashboardStickyControlsProps {
  product: Product
  /**
   * The element to watch — the sticky bar fades in once this element is no
   * longer visible in the viewport (e.g. the inline "Add to Cart" controls).
   */
  observeRef: RefObject<HTMLElement | null>
}

export const ProductDashboardStickyControls = ({
  product,
  observeRef,
}: ProductDashboardStickyControlsProps) => {
  const { sku, catNo } = product

  const page = usePage()
  const { setZendeskBottomOffset } = useZendesk()
  const rootRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  const { quantity, setQuantity, handleAddToCart, isLoading } =
    useAddToCart(product)

  // Mirror ProductMain's header offset so the sticky bar tucks below the site
  // header (which changes height with the sitewide banner / mobile drawer).
  const headerOffset = useMemo(() => {
    const headerHeight = page.pageHeaderHeight ?? 0

    if (page.pageWidth >= MIN_DESKTOP_WIDTH) {
      return headerHeight
    }

    return (
      headerHeight -
      (page.sitewideBannerHeight ?? 0) -
      (page.pageHeaderMobileDrawerHeight ?? 0)
    )
  }, [
    page.pageWidth,
    page.pageHeaderHeight,
    page.sitewideBannerHeight,
    page.pageHeaderMobileDrawerHeight,
  ])

  const headerStyle = useMemo<CSSProperties>(() => {
    return { top: `${headerOffset}px` }
  }, [headerOffset])

  useEffect(() => {
    const target = observeRef.current

    if (!target) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(!entry.isIntersecting)
      },
      // Shrink the observation root's top edge down to the header line so the
      // bar fades in the moment the target slips behind the header.
      { rootMargin: `${-headerOffset}px 0px 0px 0px`, threshold: 0 }
    )

    observer.observe(target)

    return () => {
      observer.disconnect()
    }
  }, [observeRef, headerOffset])

  // Push the Zendesk chat/launcher above the pinned sticky bar while it's
  // pinned to the bottom of the viewport (<= 1023px). The offset mirrors the
  // bar's rendered height plus its bottom gap.
  useEffect(() => {
    const isBottomPinned = page.pageWidth < MIN_DESKTOP_WIDTH
    const target = rootRef.current

    if (!target || !isBottomPinned || !isVisible) {
      setZendeskBottomOffset(0)

      return () => {
        setZendeskBottomOffset(0)
      }
    }

    const updateOffset = () => {
      const height = target.getBoundingClientRect()?.height || 0
      setZendeskBottomOffset(
        height ? height + STICKY_CONTROLS_BOTTOM_OFFSET : 0
      )
    }

    const resizeObserver = new ResizeObserver(() => {
      updateOffset()
    })

    resizeObserver.observe(target)
    updateOffset()

    return () => {
      setZendeskBottomOffset(0)
      resizeObserver.disconnect()
    }
  }, [isVisible, page.pageWidth, setZendeskBottomOffset])

  return (
    <div
      ref={rootRef}
      id='productDashboardStickyControls'
      className={classNames(styles.root, { [styles.visible]: isVisible })}
      style={headerStyle}
      aria-hidden={!isVisible}>
      <div className={styles.bar}>
        <div className={styles.info}>
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegularBold}
            className={styles.title}>
            {product?.title}
          </Typography>
          {catNo && (
            <Typography
              tag={TypographyTag.span}
              variant={TypographyVariant.BodySmall}
              className={styles.catNo}>
              {catNo}
            </Typography>
          )}
        </div>
        <div className={styles.controls}>
          <ProductDashboardQuantitySelector
            product={product}
            quantity={quantity}
            onChange={setQuantity}
          />
          <Button
            className={styles.addToCartButton}
            disabled={!sku}
            loading={isLoading}
            onClick={() => {
              handleAddToCart()
            }}>
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  )
}
